import React, {forwardRef, useEffect, useLayoutEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ReactHtmlParser from 'react-html-parser';
import {combineClassNames, createAnalyticsItemsList, reactParserCommonOptions} from '../../../../app/functions';

import Validate from '../../../../app/validation/validation';
import './index.scss';
import * as Actions from '../../../../redux/actions';
import {submitBenefitLeadForm} from '../../../../redux/actions';

import Checkbox from 'components/forms/checkbox';
import checkedCheckboxImagePath from "../../../../assets/icons/cb_checked.svg";
import emptyCheckboxImagePath from "../../../../assets/icons/cb_empty.svg";
import analytics from 'app/analytics';
import * as Popups from "../../../../constants/popup-types";
import AnalyticsManager from "../../../../app/analytics";
import {UpsalePopupStatus} from '../../../../app/enums';

const LEAD_FORN_CHECKBOX_ERROR_LABEL = 'lead_form_checkbox_error_label'

const LeadForm = forwardRef(({isVisible, settings, benefit}, containerRef) => {
    const dispatch = useDispatch();


    const userData = useSelector((store) => store.config.UserData);
    const translations = useSelector((store) => store.config.Translations);
    const facebookLeads = useSelector((s) => s.config.Parameters.facebookLeads);

    const initForm =(settings, userData) => {
        const initialState = {};

        const createFieldState = (fieldName, initialValue, fieldRules, fieldOptions = []) => ({
            name: fieldName,
            value: initialValue,
            rules: fieldRules,
            options: fieldOptions,
            isValid: false,
            errorMessage: '',
        });

        if (settings.includeConsentCheckbox) {
            initialState['consentCheckbox'] = createFieldState(
                settings.consentCheckboxContent,
                false,
                ['checked_checkbox'],
                [],
                true,
            );
        }
        if (!userData?.maStatus && settings.includeMarketingCheckbox) {
            initialState['marketingCheckbox'] = createFieldState(
                settings.marketingCheckboxContent,
                false,
                ['checked_checkbox'],
                [],
            );
        }
        if (settings.includeName) {
            initialState['name'] = createFieldState(
                translations['benefit_lead_form_name_label'],
                userData ? `${userData['first_name']} ${userData['last_name']}` : '',
                ['full_name', 'not_empty'],
            );
        }

        if (settings.includePhone) {
            initialState['phone'] = createFieldState(
                translations['benefit_lead_form_phone_label'],
                userData['maPhone'] || userData['phone'] || '',
                ['phone', 'not_empty'],
            )
        }
        if (userData === false && !settings.hideRegularIdField) {
            initialState['personalId'] = createFieldState(
                translations['register_screen_id_placehlder'],
                '',
                ['personal_id', 'not_empty'],
            );
        }

        if (settings.includeEmail) {
            initialState['email'] = createFieldState(
                translations['benefit_lead_form_email_label'],
                userData['maEmail'] || userData['email'] || '',
                ['email', 'not_empty'],
            );
        }

        if (settings.includeFirstDynamicField) {
            initialState['firstDynamicField'] = createFieldState(
                settings.firstDynamicFieldName,
                '',
                ['not_empty'],
                settings.firstDynamicFieldOptions,
            );
        }

        if (settings.includeSecondDynamicField) {
            initialState['secondDynamicField'] = createFieldState(
                settings.secondDynamicFieldName,
                '',
                ['not_empty'],
                settings.secondDynamicFieldOptions,
            );
        }

        return initialState;
    }

    const [formState, setFormState] = useState( ()=> initForm(settings, userData));
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);



    useEffect(() => {

        setFormState(initForm(settings, userData))

    }, [userData]);

    const handleInputChange = (event) => {
        const {name, value} = event.target;

        setFormState({
            ...formState,
            [name]: {...formState[name], value},
        });
    };

    const handleFormSubmit = (event) => {
        event.preventDefault();

        let formHasErrors = false;

        const payload = {};
        const newFormState = {...formState};

        Object.keys(formState).forEach((fieldKey) => {
            payload[fieldKey] = formState[fieldKey]['value'];

            const validator = Validate(formState[fieldKey]['value'], formState[fieldKey]['rules']);
            const {valid: isValid, msg: errorMessage} = validator;

            if (!isValid) formHasErrors = true;

            newFormState[fieldKey] = {...newFormState[fieldKey], isValid, errorMessage};
        });

        if (userData?.maStatus && settings.includeMarketingCheckbox) {
            payload['marketingCheckbox'] = true;
        }

        if (formHasErrors) {
            setFormState(newFormState);
            return;
        }

        if (window.fbq) {
            if (String(settings.benefitId) === '45310' && !facebookLeads?.[String(settings.benefitId)])
                window.fbq('track', 'Lead_freesbe');
            if (String(settings.benefitId) in facebookLeads) {
                window.fbq('track', facebookLeads[String(settings.benefitId)])
            }
        }

        dispatch(submitBenefitLeadForm({
            benefitId: settings.benefitId,
            payload: JSON.stringify(payload).replace(/\\/g, '\\\\'),

        }, (response) => {
            AnalyticsManager
            .logLeadSubmitSuccess(createAnalyticsItemsList([{...benefit}]))

            if (benefit?.relatedBenefits?.length > 0 && response.marketingStatus) {
                dispatch(Actions.addPopup({
                    type: Popups.SUBSIDY_POPUP,
                    content: response.marketingStatus,
                    header: benefit.main.title,
                    benefits: benefit.relatedBenefits,
                    status: UpsalePopupStatus.NO_INVENTORY,
                    footerText: benefit.main.upsell_popup_footer_text
                }))
            }
            else if (response.marketingStatus && !benefit?.relatedBenefits?.length) {
                dispatch(Actions.addPopup({
                    type: Popups.GENERAL_MESSAGE,
                    text: response.marketingStatus,
                }));
            }
            setIsFormSubmitted(true);
            if (userData) {
                userData.maStatusaStatus = true;
            }
        }));


    };

    useLayoutEffect(() => {
        if (!isVisible && !isFormSubmitted) return;

        setTimeout(() => {
            if (containerRef.current) {
                containerRef.current.scrollIntoView({block: 'center'});
            }
        }, 250);
    }, [isVisible, isFormSubmitted]);


    const handleConsentCheckboxChange = (newValue) => {
        const validation = Validate(newValue, formState.consentCheckbox.rules);

        setFormState((prevState) => ({
            ...prevState,
            consentCheckbox: {
                ...prevState.consentCheckbox,
                value: newValue,
                isValid: validation.valid,
                errorMessage: validation.msg,
            },
        }));
    }
    const handleMarketingApprovalCheckboxChange = (newValue) => {
        const validation = Validate(newValue, formState.marketingCheckbox.rules);

        setFormState((prevState) => ({
            ...prevState,
            marketingCheckbox: {
                ...prevState.marketingCheckbox,
                value: newValue,
                isValid: validation.valid,
                errorMessage: validation.msg,
            },
        }));
    }
    return (
        <div
            ref={containerRef}
            className={combineClassNames(
                'benefit-lead-form-container',
                isVisible && 'benefit-lead-form-container_visible',
            )}
        >
            <div className="benefit-lead-form">
                {isFormSubmitted ? (
                    <div className="form-submitted-message">
                        {ReactHtmlParser(settings.submitMessage, reactParserCommonOptions())}
                    </div>
                ) : (
                    <form onSubmit={handleFormSubmit}>
                        <div className="form-fields-row">
                            {/* Render other form fields */}
                            {Object.keys(formState).map((fieldKey) => {
                                const uniqueId = `benefitLeadForm_${fieldKey}`;
                                const fieldState = formState[fieldKey];

                                if (fieldKey === 'consentCheckbox' || fieldKey === 'marketingCheckbox') {
                                    return null;
                                }

                                return (
                                    <div key={uniqueId} className="form-field-container">
                                        <label htmlFor={uniqueId} className="form-field-label">
                                            {fieldState.name}
                                        </label>

                                        {fieldState.options.length > 0 ? (
                                            <select
                                                id={uniqueId}
                                                name={fieldKey}
                                                value={fieldState.value}
                                                onChange={handleInputChange}
                                                className="form-field-select"
                                            >
                                                <option value="">
                                                    {translations['benefit_lead_form_select_option']}
                                                </option>

                                                {fieldState.options.map((optionValue, optionIndex) => (
                                                    <option key={optionIndex} value={optionValue}>
                                                        {optionValue}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                id={uniqueId}
                                                name={fieldKey}
                                                value={fieldState.value}
                                                onChange={handleInputChange}
                                                className="form-field-input"
                                            />
                                        )}

                                        {fieldState.errorMessage !== '' && (
                                            <p className="form-field-error">
                                                {fieldState.errorMessage}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="form-inline-container">
                            {settings.includeConsentCheckbox && <div className='checkboxContainer'>
                                <Checkbox
                                    name="phoneCall_agreement"
                                    value={formState.consentCheckbox.value}
                                    label={formState.consentCheckbox.name}
                                    checked_img={checkedCheckboxImagePath}
                                    not_checked_img={emptyCheckboxImagePath}
                                    className="form-checkbox-input"
                                    onChange={handleConsentCheckboxChange}
                                />
                                <p className="form-field-error">
                                    {formState.consentCheckbox.errorMessage}
                                </p>
                            </div>}
                            {!userData.maStatus && settings.includeMarketingCheckbox &&
                                <div className='checkboxContainer'>
                                    <Checkbox
                                        name="marketing_approval"
                                        value={formState.marketingCheckbox.value}
                                        label={formState.marketingCheckbox.name}
                                        checked_img={checkedCheckboxImagePath}
                                        not_checked_img={emptyCheckboxImagePath}
                                        className="form-checkbox-input"
                                        onChange={handleMarketingApprovalCheckboxChange}
                                    />
                                    <p className="form-field-error">
                                        {formState.marketingCheckbox.errorMessage}
                                    </p>
                                </div>
                            }
                        </div>
                        <button type="submit" className="form-submit-button">
                            {settings.submitButton}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
});

export default LeadForm;
